import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NetworkStatus } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  clampRectToSpan,
  diffMoves,
  findFreeSlot,
  moveItem,
  removeItem,
  toastManager,
  useWidgetRegistry,
  type GridLayoutItem,
  type GridRect,
  type WidgetConfig,
  type WidgetInstance,
  type WidgetMove,
  type WidgetPreset,
  DEFAULT_GRID_COLUMNS,
} from "@salestrak/ui";

import {
  AddWidgetDocument,
  DuplicateWidgetDocument,
  MoveWidgetsDocument,
  RemoveWidgetDocument,
  ReplaceWidgetDocument,
  UpdateWidgetConfigDocument,
  type WidgetRow,
  WidgetsDocument,
} from "#app/lib/data";
import { diffWidgetBoards } from "#app/widgets/dashboards/widget-board-diff";
import {
  parsePersonalSnapshot,
  PERSONAL_SNAPSHOT_VERSION,
  type WidgetViewPersonalSnapshot,
} from "#app/widgets/dashboards/widget-view-snapshot";
import { useAppData } from "#app/lib/data";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

/**
 * THE widget-view store: one hook owning a view's widgets — load, add,
 * duplicate, move, config, remove — for any view key. A widget view is a
 * named collection of placed widgets: rows in the one `widget` table under a
 * key. The composing feature derives the key and never
 * hand-wires persistence: dashboards pass their scope-derived keys
 * (`"widget-view-<kind>:<id>"` — persisted VALUES, do NOT rename or existing
 * dashboards orphan).
 *
 * Widgets are persisted through the core GraphQL widgets API as discrete rows
 * keyed by `(workspace, view)`. Who may write a view is the ownership model
 * (`use-widget-view-access.ts` / the server's WidgetViewAccessService — one
 * shared grammar); this store handles WHERE writes go, via three modes:
 *
 * - **`autosave`** (default — private views like My Work): every op writes
 *   through immediately, exactly the original behavior. The server query
 *   seeds a local working copy that is authoritative for the session; adds
 *   optimistically append a temp widget and swap in the server id; config
 *   edits debounce into a single wholesale-replace.
 * - **`staged`** (shared views, for editors): every op mutates the working
 *   copy ONLY. `dirty` flips, and `saveToTeam()` diff-commits the working
 *   copy against the canonical baseline (removes → adds → replaces →
 *   configs → one move batch); `discardChanges()` restores the baseline.
 *   The `direct` escape hatch keeps writing through for the managed
 *   maintenance effects (seeding / duplicate repair / layout migration) —
 *   corrections to the CANONICAL board, never user edits.
 * - **`personal`** (shared views, any viewer whose lens is on their personal
 *   fork): ops mutate the working copy and a debounced write persists the
 *   ENTIRE board as a private fork in the user-scoped settings bag
 *   (`widget-view-personal:<view>` — never widget rows). The first change
 *   creates the fork; `resetToDefault()` deletes it and re-seeds the working
 *   copy from the shared rows — the viewer stays on the personal lens (the
 *   host keeps Personalize armed) and the next edit re-creates the fork.
 *   No widget mutation ever fires in this mode. A mode change
 *   with the view unchanged (the Personal ↔ Shared lens switch) re-seeds the
 *   working copy from the right source, flushing any pending fork write
 *   first.
 */

const CONFIG_DEBOUNCE_MS = 500;

/**
 * A predefined widget placed at an EXPLICIT grid position — the unit
 * `seedWidgets` writes. Unlike `addWidget`'s preset (which lands in the first
 * free slot), a seed carries its own x/y/w/h so a managed layout (a curated
 * catalog) or a dashboard template lands each widget in its designed slot.
 */
export type WidgetSeed = {
  type: string;
  config: WidgetConfig;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type WidgetViewMode = "autosave" | "staged" | "personal";

export type WidgetViewOptions = Omittable<{
  /** Where writes go — see the module doc. Defaults to `autosave`. */
  mode?: WidgetViewMode;
  /** Personal-fork settings key; defaults to `widget-view-personal:<view>`. */
  personalKey?: string;
}>;

// The fork payload's type and codec live in `widget-view-snapshot.ts` (pure,
// node-testable); re-exported here so store consumers keep one import.
export type { WidgetViewPersonalSnapshot };

export type WidgetViewStore = {
  widgets: WidgetInstance[];
  /** True until the view's widgets have been read from the server once (and,
   *  in personal mode, the fork setting has resolved). */
  initialLoading: boolean;
  /** Optimistically appends into the first free slot (or, with a
   *  marquee-drawn `area`, at the drawn cell/size clamped to the module span
   *  and settled through the grid), then swaps in the server-issued widget id
   *  when the mutation resolves. Staged/personal: local working copy only. */
  addWidget: (preset: WidgetPreset, area?: GridRect) => void;
  /** Place predefined widgets at their explicit positions (managed catalog
   *  seed / toggle-on / template). Batches one optimistic append with one
   *  `addWidget` mutation per seed; widgets whose `config.widgetKey` is
   *  already placed are skipped so the operation is idempotent.
   *  Staged/personal: local working copy only. */
  seedWidgets: (seeds: WidgetSeed[]) => void;
  /** Same optimistic shape as addWidget. */
  duplicateWidget: (widget: WidgetInstance) => void;
  /** Swaps a placed widget for a different module in place — same id and grid
   *  cell, the preset's type/config/schemaVersion. Optimistic; reverts on
   *  failure. */
  replaceWidget: (id: string, preset: WidgetPreset) => void;
  /** Removes the widget AND gravity-compacts the survivors in one optimistic
   *  commit (the FLIP spring animates them up). Returns the post-remove
   *  layout so the host can record it into its layout history for undo.
   *  Every user-facing removal compacts — managed toggles included. Only the
   *  canonical maintenance corrections pass `{ compact: false }`: removing an
   *  overlapping duplicate or retiring a migrated row must not shuffle the
   *  board mid-correction (the migration's own moves and the gravity
   *  self-heal pass settle it after). */
  removeWidget: (id: string, opts?: { compact?: boolean }) => GridLayoutItem[];
  updateWidgetConfig: (id: string, patch: WidgetConfig) => void;
  moveWidgets: (moves: WidgetMove[]) => void;
  /** Undo/redo restore: replace the whole board in ONE local commit, then
   *  persist per mode — staged flips the save bar, personal schedules the
   *  fork write, autosave replays the diff against the server. */
  restoreBoard: (next: WidgetInstance[]) => void;
  /** Staged mode: the working copy has user edits the team can't see yet. */
  dirty: boolean;
  /** Staged mode: diff-commit the working copy to the shared board —
   *  removes → adds (capturing server ids) → replaces → configs → one move
   *  batch. On failure: toast, re-sync the baseline from the server, KEEP
   *  the working copy so the user can retry. */
  saveToTeam: () => Promise<void>;
  /** Staged mode: throw the working copy away, back to the canonical board. */
  discardChanges: () => void;
  /** Staged mode: keep the dirty working copy as the viewer's PERSONAL fork
   *  instead of committing it to the shared board — the "Save for just me"
   *  destination. Writes the full board into the fork setting (no widget
   *  mutation fires; the shared rows are untouched), then restores the
   *  template working copy to the canonical baseline. The host flips its
   *  lens to personal so the viewer keeps looking at exactly what they
   *  built. Local `temp-` ids are safe inside a fork — personal mode never
   *  round-trips ids to the server. */
  saveAsPersonal: () => void;
  /** Personal mode: a persisted fork exists for this view. */
  hasPersonalLayer: boolean;
  /** Personal mode: delete the fork and re-seed from the shared board. */
  resetToDefault: () => void;
  /** Write-through escape hatch for the managed-host maintenance effects
   *  (duplicate repair, `managedLayoutVersion` migration, gravity self-heal):
   *  they produce the CANONICAL default, not a user change, so they bypass
   *  staging and never flip `dirty`. In autosave mode these are simply the
   *  normal ops (config skips the debounce). */
  direct: {
    seed: (seeds: WidgetSeed[]) => void;
    remove: (id: string, opts?: { compact?: boolean }) => void;
    updateConfig: (id: string, patch: WidgetConfig) => void;
    move: (moves: WidgetMove[]) => void;
  };
};

function toWidget(w: WidgetRow): WidgetInstance {
  return {
    id: w.id,
    type: w.type,
    config: (w.config ?? null) as WidgetConfig | null,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    schemaVersion: w.schemaVersion,
  };
}

/**
 * Local widget id used while an add/duplicate hasn't reached the server —
 * either mid-mutation (autosave: `temp-`, replaced by the server id on
 * resolve) or living purely in the working copy (staged adds stay `temp-`
 * until `saveToTeam`; personal-fork adds get `pw-` and NEVER round-trip to
 * the widget API). `crypto.randomUUID` is secure-context only (absent over
 * plain http / inside the iOS shell), so fall back to `getRandomValues`,
 * then time+random — local uniqueness is all a placeholder needs.
 */
function localWidgetId(prefix: string): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return `${prefix}-${c.randomUUID()}`;
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return `${prefix}-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
  }
  return `${prefix}-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** Column count of the widget grid — matches `WidgetGrid`'s default. New
 *  widgets auto-place into the first free slot at this width via `findFreeSlot`;
 *  the dashboard's Tidy action packs at the same width. */
// Derived from the library grid default (D12) — the view store's placement math
// must use the same column count the grid renders at.
export const GRID_COLUMNS = DEFAULT_GRID_COLUMNS;

/**
 * The starting config for a widget created from a preset — the ONE place the
 * naming rule lives (both addWidget and replaceWidget call it). A curated preset
 * lands named (its gallery label becomes the title) unless it already declares a
 * title OR opts out with `autoTitle: false` (note, embeds, blank cards). The flag
 * is the explicit override; when unset it falls back to the section rule (Custom
 * presets stay untitled) so unflagged presets keep their behavior. The flag is
 * what stops drift: without it, moving an embed preset into a non-Custom
 * section would silently give it an unwanted title. (U2)
 */
function presetInitialConfig(preset: WidgetPreset): WidgetConfig {
  const config = { ...preset.defaultConfig } as WidgetConfig;
  const autoTitle = preset.autoTitle ?? preset.section !== "Custom";
  if (autoTitle && typeof config.title !== "string") {
    config.title = preset.label;
  }
  return config;
}

export function useWidgetView(view: string, opts?: WidgetViewOptions): WidgetViewStore {
  const registry = useWidgetRegistry();
  const { currentUser } = useAppData();
  const mode = opts?.mode ?? "autosave";
  // Staged and personal ops never write through — they land in the working
  // copy and reach persistence via saveToTeam / the personal fork instead.
  const localOnly = mode !== "autosave";
  const localIdPrefix = mode === "personal" ? "pw" : "temp";
  const personalKey = opts?.personalKey ?? `widget-view-personal:${view}`;
  const personal = useUserSetting(personalKey, parsePersonalSnapshot, {
    skip: mode !== "personal",
  });

  const [addWidgetMutation] = useMutation(AddWidgetDocument);
  const [duplicateWidgetMutation] = useMutation(DuplicateWidgetDocument);
  const [replaceWidgetMutation] = useMutation(ReplaceWidgetDocument);
  const [updateWidgetConfigMutation] = useMutation(UpdateWidgetConfigDocument);
  const [moveWidgetsMutation] = useMutation(MoveWidgetsDocument);
  const [removeWidgetMutation] = useMutation(RemoveWidgetDocument);

  const variables = useMemo(() => ({ view }), [view]);
  const { data, networkStatus, refetch } = useQuery(WidgetsDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !currentUser,
  });

  // Local working copy: `null` until seeded (server rows, or the personal
  // fork when one exists).
  const [widgets, setWidgetsState] = useState<WidgetInstance[] | null>(null);
  // Mirror in a ref so callbacks read the latest list without re-subscribing.
  const widgetsRef = useRef<WidgetInstance[]>([]);

  // Staged mode: `dirty` marks user edits the team can't see yet. Held in a
  // ref too so `applyWidgets` (below) can consult it synchronously.
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const setDirty = useCallback((next: boolean) => {
    dirtyRef.current = next;
    setDirtyState(next);
  }, []);

  // The canonical board as last known from the server. While the working
  // copy is CLEAN it tracks every commit (including the async temp→server id
  // swaps of write-through ops); the first staged edit freezes it — that
  // frozen snapshot is what `saveToTeam` diffs against and what
  // `discardChanges` restores.
  const baselineRef = useRef<WidgetInstance[]>([]);

  const applyWidgets = useCallback((next: WidgetInstance[]) => {
    widgetsRef.current = next;
    setWidgetsState(next);
    if (!dirtyRef.current) baselineRef.current = next;
  }, []);

  // ── Personal-fork persistence: debounced full-board snapshot ────────────
  // Every personal-mode op schedules the CURRENT working copy; the debounce
  // coalesces a burst (a drag emits one commit, config edits several) into
  // one settings write. The first write creates the fork. Only the LATEST
  // pending list may write — a reset clears the pending slot so a stale
  // debounced value can't resurrect the fork.
  const [personalPending, setPersonalPending] = useState<WidgetInstance[] | null>(null);
  const personalPendingRef = useRef<WidgetInstance[] | null>(null);
  const personalSentRef = useRef<WidgetInstance[] | null>(null);
  const debouncedPersonal = useDebouncedValue(personalPending, CONFIG_DEBOUNCE_MS);
  const setPersonalValue = personal.setValue;

  const schedulePersonal = useCallback(() => {
    const next = widgetsRef.current;
    personalPendingRef.current = next;
    setPersonalPending(next);
  }, []);

  // Write the pending fork snapshot if it hasn't been sent yet — THE one
  // spelling of the guarded fork write (the debounce landing and the
  // unmount/view-change flush both call it; the lens-switch flush parks its
  // snapshot in `lensFlushRef` below because its pending slot is cleared
  // during render, but sends the same payload shape). The sent-ref guard
  // keeps every caller idempotent (StrictMode double-invocations included).
  const flushPersonalFork = useCallback(() => {
    const pending = personalPendingRef.current;
    if (pending && pending !== personalSentRef.current) {
      personalSentRef.current = pending;
      setPersonalValue({ version: PERSONAL_SNAPSHOT_VERSION, widgets: pending });
    }
  }, [setPersonalValue]);

  useEffect(() => {
    if (mode !== "personal" || !debouncedPersonal) return;
    if (debouncedPersonal !== personalPendingRef.current) return; // superseded or reset
    flushPersonalFork();
  }, [mode, debouncedPersonal, flushPersonalFork]);

  // A pending fork snapshot the render-phase mode reset (below) parked for the
  // post-commit flush effect — the reset clears the pending slots synchronously,
  // so the snapshot must be carried across the commit somewhere the effect can
  // still read it.
  const lensFlushRef = useRef<WidgetInstance[] | null>(null);

  // Flush a personal edit still inside the debounce window when the hook
  // unmounts or the view/mode changes, so a tweak made <500ms before
  // navigating away still reaches the fork.
  useEffect(() => {
    if (mode !== "personal") return;
    return () => flushPersonalFork();
  }, [mode, view, flushPersonalFork]);

  // Two hooks bracket every local (non-write-through) edit:
  //
  // `beginLocalEdit` — BEFORE the edit's `applyWidgets`. Staged mode flips
  // `dirty` first so the baseline freezes at the pre-edit board; calling it
  // after would let `applyWidgets` sync the edit INTO the baseline, and the
  // first staged edit of a session would then survive Discard and vanish
  // from the save diff.
  //
  // `markLocalEdit` — AFTER the edit's `applyWidgets`. Personal mode
  // schedules the fork write, which snapshots the post-edit working copy.
  const beginLocalEdit = useCallback(() => {
    if (mode === "staged" && !dirtyRef.current) setDirty(true);
  }, [mode, setDirty]);
  const markLocalEdit = useCallback(() => {
    if (mode === "staged" && !dirtyRef.current) setDirty(true);
    if (mode === "personal") schedulePersonal();
  }, [mode, setDirty, schedulePersonal]);

  // Re-seed when the view changes — each view reads its own widgets.
  const viewRef = useRef(view);
  if (view !== viewRef.current) {
    viewRef.current = view;
    widgetsRef.current = [];
    baselineRef.current = [];
    dirtyRef.current = false;
    personalPendingRef.current = null;
    personalSentRef.current = null;
    setWidgetsState(null);
    setDirtyState(false);
    setPersonalPending(null);
  }

  // Re-seed when the MODE changes with the view unchanged (the Personal ↔
  // Shared lens switch): one lens's working copy — and any
  // staged dirty/baseline state — must never leak into the other. Runs AFTER
  // the view reset so a simultaneous view+mode change never flushes to the
  // wrong fork key. The re-seed must NOT trust the cached query rows: this
  // session's own writes (an editor's saves) never update the WidgetsDocument
  // cache — the working copy is authoritative — so `pendingModeRefetch` holds
  // seeding until a refetch lands fresh canonical rows.
  const [pendingModeRefetch, setPendingModeRefetch] = useState(false);
  const modeRef = useRef(mode);
  if (mode !== modeRef.current) {
    modeRef.current = mode;
    setPendingModeRefetch(true);
    // Leaving personal mode mid-debounce: the pending fork snapshot must still
    // reach the fork — but the write is an Apollo mutation whose synchronous
    // cache broadcast setStates other mounted consumers, so it must NEVER fire
    // during render (and a discarded concurrent render would still hit the
    // network). Park the unsent snapshot here; the post-commit effect below
    // flushes it. (The unmount-flush effect's cleanup can't cover this: it
    // runs after this render, when the pending slot below is already cleared.)
    // The key is unchanged on a mode-only switch, so the flush addresses the
    // right fork.
    const pendingFork = personalPendingRef.current;
    if (pendingFork && pendingFork !== personalSentRef.current) {
      lensFlushRef.current = pendingFork;
    }
    widgetsRef.current = [];
    baselineRef.current = [];
    dirtyRef.current = false;
    personalPendingRef.current = null;
    personalSentRef.current = null;
    setWidgetsState(null);
    setDirtyState(false);
    setPersonalPending(null);
  }

  // Post-commit half of the lens-switch flush: persist the snapshot the
  // render-phase reset parked, once the mode change commits. Clearing the ref
  // before the write keeps the flush single-shot (StrictMode re-runs and later
  // commits see null); the reset already cleared the pending/sent slots, so no
  // other flush path can re-send this snapshot.
  useEffect(() => {
    const parked = lensFlushRef.current;
    if (!parked) return;
    lensFlushRef.current = null;
    setPersonalValue({ version: PERSONAL_SNAPSHOT_VERSION, widgets: parked });
  }, [mode, setPersonalValue]);

  // A mode change re-seeds against fresh server rows: refetch first, then let
  // the seeding effect below proceed. Failure still clears the gate — a
  // best-effort seed from the cache beats a stuck spinner.
  useEffect(() => {
    if (!pendingModeRefetch) return;
    refetch().finally(() => setPendingModeRefetch(false));
  }, [pendingModeRefetch, refetch]);

  // Seed once the query result for THIS view arrives. Apollo can briefly
  // return the previous view's rows during a variables switch, so only seed
  // when every returned row matches the current view (an empty list is a
  // legitimately empty view). `cache-and-network` can first return a stale
  // cached empty list while the server already has widgets; waiting for
  // `ready` prevents managed defaults from being seeded on top of those
  // server rows. Personal mode seeds from the FORK when one exists (team
  // updates don't reach a personalized board until Reset — by design).
  useEffect(() => {
    if (widgets !== null) return;
    if (mode === "personal") {
      if (personal.initialLoading) return;
      const fork = personal.value;
      if (fork) {
        applyWidgets(fork.widgets);
        return;
      }
    }
    if (pendingModeRefetch) return; // fresh canonical rows are on the way
    if (networkStatus !== NetworkStatus.ready) return;
    if (!data?.widgets) return;
    if (data.widgets.some((w) => w.view !== view)) return;
    applyWidgets(data.widgets.map(toWidget));
  }, [
    widgets,
    data,
    networkStatus,
    view,
    applyWidgets,
    mode,
    personal.value,
    personal.initialLoading,
    pendingModeRefetch,
  ]);

  // ── Config writes: debounced wholesale-replace ──────────────────────────
  // The settings dialog commits per key/blur; coalesce a burst into one write.
  // One pending slot holds the widget currently being edited; if editing jumps
  // to a DIFFERENT widget mid-debounce, the outgoing widget's edit is flushed
  // first (see `updateWidgetConfig`) so a second edit never silently drops the
  // first. (Autosave mode only — staged/personal config edits live in the
  // working copy until saved/forked.)
  const [pendingConfig, setPendingConfig] = useState<{
    id: string;
    config: WidgetConfig;
  } | null>(null);
  const pendingConfigRef = useRef(pendingConfig);
  pendingConfigRef.current = pendingConfig;
  const sentConfigRef = useRef<{ id: string; config: WidgetConfig } | null>(null);
  const debouncedConfig = useDebouncedValue(pendingConfig, CONFIG_DEBOUNCE_MS);

  const writeConfig = useCallback(
    (write: { id: string; config: WidgetConfig }) => {
      if (write.id.startsWith("temp-") || write.id.startsWith("pw-")) return; // not persisted
      sentConfigRef.current = write;
      updateWidgetConfigMutation({
        variables: {
          id: write.id,
          input: { config: write.config as Record<string, unknown> },
        },
      }).catch((err) => toastMutationError(err, "Failed to save widget"));
    },
    [updateWidgetConfigMutation],
  );

  useEffect(() => {
    if (!debouncedConfig || debouncedConfig === sentConfigRef.current) return;
    writeConfig(debouncedConfig);
  }, [debouncedConfig, writeConfig]);

  // Flush a config edit still inside the debounce window when the hook
  // unmounts or the view changes, so an edit made <500ms before navigating
  // away still reaches the server.
  useEffect(() => {
    return () => {
      const pending = pendingConfigRef.current;
      if (pending && pending !== sentConfigRef.current) writeConfig(pending);
    };
  }, [view, writeConfig]);

  // Merge a config patch into the working copy; returns null when no widget
  // matched (e.g. an inline editor's unmount-flush autosave firing as the
  // widget is deleted — never queue a write to a now-gone row).
  // `beforeApply` runs only when a widget matched, immediately before the
  // commit — the staged baseline-freeze hook.
  const mergeConfigLocally = useCallback(
    (id: string, patch: WidgetConfig, beforeApply?: () => void): WidgetConfig | null => {
      const target = widgetsRef.current.find((i) => i.id === id);
      if (!target) return null;
      const merged: WidgetConfig = { ...(target.config ?? {}), ...patch };
      beforeApply?.();
      applyWidgets(
        widgetsRef.current.map((i) => (i.id === id ? { ...i, config: merged } : i)),
      );
      return merged;
    },
    [applyWidgets],
  );

  const updateWidgetConfig = useCallback(
    (id: string, patch: WidgetConfig) => {
      const merged = mergeConfigLocally(id, patch, localOnly ? beginLocalEdit : undefined);
      if (merged === null) return;
      if (localOnly) {
        markLocalEdit();
        return;
      }
      // Editing jumped to a different widget while an edit to the previous one is
      // still inside the debounce window: flush that pending edit now so the single
      // pending slot can be reused for this widget without dropping the prior write.
      const outgoing = pendingConfigRef.current;
      if (outgoing && outgoing.id !== id && outgoing !== sentConfigRef.current) {
        writeConfig(outgoing);
      }
      const next = { id, config: merged };
      // Keep the flush ref authoritative synchronously: when this edit arrives via a
      // widget's unmount cleanup during a hard navigation, the scheduled re-render
      // never happens, so the render-time `pendingConfigRef.current = pendingConfig`
      // sync is skipped — writing it here lets the unmount/view-change flush still
      // catch it.
      pendingConfigRef.current = next;
      setPendingConfig(next);
    },
    [mergeConfigLocally, localOnly, beginLocalEdit, markLocalEdit, writeConfig],
  );

  // Maintenance-effect config write: immediate (no debounce) and always
  // through — the managed layout migration rewrites a handful of configs to
  // the canonical seeds in one pass.
  const directUpdateConfig = useCallback(
    (id: string, patch: WidgetConfig) => {
      const merged = mergeConfigLocally(id, patch);
      if (merged === null) return;
      writeConfig({ id, config: merged });
    },
    [mergeConfigLocally, writeConfig],
  );

  // ── Move/resize: one mutation per gesture commit ────────────────────────

  // Apply a move batch to the working copy — THE one spelling of "a move lands
  // on the board" (write-through and local-only paths both call it, so staged/
  // personal moves can never diverge from committed ones). Returns the
  // pre-move list for the write-through path's failure revert.
  const applyMovesToWorking = useCallback(
    (moves: WidgetMove[]): WidgetInstance[] => {
      const byId = new Map(moves.map((m) => [m.id, m]));
      const prev = widgetsRef.current;
      applyWidgets(
        prev.map((i) => {
          const m = byId.get(i.id);
          return m ? { ...i, x: m.x, y: m.y, w: m.w, h: m.h } : i;
        }),
      );
      return prev;
    },
    [applyWidgets],
  );

  const moveWidgetsThrough = useCallback(
    (moves: WidgetMove[]) => {
      const prev = applyMovesToWorking(moves);
      // Skip ids that aren't persisted yet (a widget mid-add); the server
      // ignores unknown ids anyway, but this avoids needless round-trips.
      const persisted = moves.filter((m) => !m.id.startsWith("temp-"));
      if (persisted.length === 0) return;
      moveWidgetsMutation({ variables: { moves: persisted } }).catch((err) => {
        applyWidgets(prev); // revert on failure
        toastMutationError(err, "Failed to save layout");
      });
    },
    [applyMovesToWorking, applyWidgets, moveWidgetsMutation],
  );

  const moveWidgets = useCallback(
    (moves: WidgetMove[]) => {
      if (localOnly) {
        beginLocalEdit();
        applyMovesToWorking(moves);
        markLocalEdit();
        return;
      }
      moveWidgetsThrough(moves);
    },
    [localOnly, beginLocalEdit, applyMovesToWorking, markLocalEdit, moveWidgetsThrough],
  );

  // ── Add / duplicate / remove ────────────────────────────────────────────
  const addWidget = useCallback(
    async (preset: WidgetPreset, area?: GridRect): Promise<void> => {
      const list = widgetsRef.current;
      const tempId = localWidgetId(localIdPrefix);
      const def = registry.get(preset.type);
      const schemaVersion = def?.schemaVersion ?? 1;
      const layout = list.map((i) => ({ id: i.id, x: i.x, y: i.y, w: i.w, h: i.h }));
      let cell: GridRect;
      let siblingMoves: WidgetMove[] = [];
      if (area) {
        // Marquee-drawn placement: clamp the drawn rect to the module span,
        // then SETTLE through the grid — the clamp can grow the rect past its
        // free area, so never insert raw (identity when it fits, honest
        // sibling reflow when it doesn't).
        const rect = clampRectToSpan(area, def?.span ?? {}, GRID_COLUMNS);
        const settled = moveItem(
          [...layout, { id: tempId, ...rect }],
          tempId,
          rect.x,
          rect.y,
          GRID_COLUMNS,
          "gravity",
        );
        cell = settled.find((i) => i.id === tempId) ?? rect;
        siblingMoves = diffMoves(
          layout,
          settled.filter((i) => i.id !== tempId),
        );
      } else {
        const w = preset.defaultW ?? 4;
        const h = preset.defaultH ?? 4;
        cell = { ...findFreeSlot(list, w, h, GRID_COLUMNS), w, h };
      }
      const config = presetInitialConfig(preset);
      // ONE local commit: the insert and its sibling reflow land together.
      const moveById = new Map(siblingMoves.map((m) => [m.id, m]));
      const restoreById = new Map(list.map((i) => [i.id, i]));
      if (localOnly) beginLocalEdit();
      applyWidgets([
        ...list.map((i) => {
          const m = moveById.get(i.id);
          return m ? { ...i, x: m.x, y: m.y, w: m.w, h: m.h } : i;
        }),
        { id: tempId, type: preset.type, config, ...cell, schemaVersion },
      ]);
      if (localOnly) {
        markLocalEdit();
        return;
      }
      const revert = () =>
        applyWidgets(
          widgetsRef.current
            .filter((i) => i.id !== tempId)
            .map((i) => {
              const orig = moveById.has(i.id) ? restoreById.get(i.id) : undefined;
              return orig ? { ...i, x: orig.x, y: orig.y, w: orig.w, h: orig.h } : i;
            }),
        );
      try {
        const res = await addWidgetMutation({
          variables: {
            view,
            input: {
              type: preset.type,
              config: config as Record<string, unknown>,
              x: cell.x,
              y: cell.y,
              w: cell.w,
              h: cell.h,
              schemaVersion,
            },
          },
        });
        const saved = res.data?.addWidget;
        if (!saved) {
          revert();
          toastManager.add({ title: "Failed to add widget", type: "error" });
          return;
        }
        applyWidgets(
          widgetsRef.current.map((i) => (i.id === tempId ? toWidget(saved) : i)),
        );
        // Add-then-moves ordering (the removeWidget precedent): a failed add
        // never persists the sibling reflow.
        const persisted = siblingMoves.filter((m) => !m.id.startsWith("temp-"));
        if (persisted.length > 0) {
          moveWidgetsMutation({ variables: { moves: persisted } }).catch((err) =>
            toastMutationError(err, "Failed to save layout"),
          );
        }
      } catch (err) {
        revert();
        toastMutationError(err, "Failed to add widget");
      }
    },
    [
      view,
      registry,
      addWidgetMutation,
      moveWidgetsMutation,
      applyWidgets,
      localOnly,
      localIdPrefix,
      beginLocalEdit,
      markLocalEdit,
    ],
  );

  // Stage seeds into the working copy (dedup by `config.widgetKey` so rapid
  // toggles/effects can't enqueue the same catalog widget twice). Returns the
  // staged descriptors so the write-through path can fire their mutations.
  // `beforeApply` runs only when unique seeds remain, immediately before the
  // commit — the staged baseline-freeze hook.
  const appendSeeds = useCallback(
    (seeds: WidgetSeed[], beforeApply?: () => void) => {
      const usedKeys = new Set(
        widgetsRef.current.flatMap((widget) => {
          const key = (widget.config as { widgetKey?: unknown } | null)?.widgetKey;
          return typeof key === "string" ? [key] : [];
        }),
      );
      const uniqueSeeds = seeds.filter((seed) => {
        const key = (seed.config as { widgetKey?: unknown }).widgetKey;
        if (typeof key !== "string") return true;
        if (usedKeys.has(key)) return false;
        usedKeys.add(key);
        return true;
      });
      if (uniqueSeeds.length === 0) return [];
      const staged = uniqueSeeds.map((seed) => ({
        tempId: localWidgetId(localIdPrefix),
        seed,
        schemaVersion: registry.get(seed.type)?.schemaVersion ?? 1,
      }));
      beforeApply?.();
      applyWidgets([
        ...widgetsRef.current,
        ...staged.map(({ tempId, seed, schemaVersion }) => ({
          id: tempId,
          type: seed.type,
          config: { ...seed.config } as WidgetConfig,
          x: seed.x,
          y: seed.y,
          w: seed.w,
          h: seed.h,
          schemaVersion,
        })),
      ]);
      return staged;
    },
    [registry, applyWidgets, localIdPrefix],
  );

  // Seed predefined widgets at explicit positions (managed catalog /
  // templates). Same optimistic pattern as `addWidget`, but batched — append
  // every temp widget at its designed x/y first, then fire one `addWidget`
  // per seed and swap each temp→real id as it resolves. A seed that fails
  // drops only its own temp.
  const seedWidgetsThrough = useCallback(
    (seeds: WidgetSeed[]) => {
      if (seeds.length === 0) return;
      const staged = appendSeeds(seeds);
      staged.forEach(({ tempId, seed, schemaVersion }) => {
        addWidgetMutation({
          variables: {
            view,
            input: {
              type: seed.type,
              config: seed.config as Record<string, unknown>,
              x: seed.x,
              y: seed.y,
              w: seed.w,
              h: seed.h,
              schemaVersion,
            },
          },
        })
          .then((res) => {
            const saved = res.data?.addWidget;
            if (!saved) {
              applyWidgets(widgetsRef.current.filter((i) => i.id !== tempId));
              toastManager.add({ title: "Failed to add widget", type: "error" });
              return;
            }
            applyWidgets(
              widgetsRef.current.map((i) => (i.id === tempId ? toWidget(saved) : i)),
            );
          })
          .catch((err) => {
            applyWidgets(widgetsRef.current.filter((i) => i.id !== tempId));
            toastMutationError(err, "Failed to add widget");
          });
      });
    },
    [view, addWidgetMutation, applyWidgets, appendSeeds],
  );

  const seedWidgets = useCallback(
    (seeds: WidgetSeed[]) => {
      if (seeds.length === 0) return;
      if (localOnly) {
        if (appendSeeds(seeds, beginLocalEdit).length > 0) markLocalEdit();
        return;
      }
      seedWidgetsThrough(seeds);
    },
    [localOnly, appendSeeds, beginLocalEdit, markLocalEdit, seedWidgetsThrough],
  );

  const duplicateWidget = useCallback(
    async (widget: WidgetInstance): Promise<void> => {
      if (localOnly) {
        const list = widgetsRef.current;
        const { x, y } = findFreeSlot(list, widget.w, widget.h, GRID_COLUMNS);
        beginLocalEdit();
        applyWidgets([
          ...list,
          {
            ...widget,
            id: localWidgetId(localIdPrefix),
            config: widget.config ? { ...widget.config } : null,
            x,
            y,
          },
        ]);
        markLocalEdit();
        return;
      }
      if (widget.id.startsWith("temp-")) return; // source not persisted yet
      const list = widgetsRef.current;
      const tempId = localWidgetId("temp");
      const { x, y } = findFreeSlot(list, widget.w, widget.h, GRID_COLUMNS);
      applyWidgets([
        ...list,
        {
          ...widget,
          id: tempId,
          config: widget.config ? { ...widget.config } : null,
          x,
          y,
        },
      ]);
      try {
        const res = await duplicateWidgetMutation({ variables: { id: widget.id } });
        const saved = res.data?.duplicateWidget;
        if (!saved) {
          applyWidgets(widgetsRef.current.filter((i) => i.id !== tempId));
          toastManager.add({ title: "Failed to duplicate widget", type: "error" });
          return;
        }
        // The server parks a duplicate below the stack; keep the optimistic
        // first-free-slot placement and persist it through the normal move
        // path so the widget doesn't teleport when the mutation resolves.
        applyWidgets(
          widgetsRef.current.map((i) =>
            i.id === tempId ? { ...toWidget(saved), x, y } : i,
          ),
        );
        if (saved.x !== x || saved.y !== y) {
          moveWidgetsMutation({
            variables: { moves: [{ id: saved.id, x, y, w: saved.w, h: saved.h }] },
          }).catch((err) => toastMutationError(err, "Failed to save layout"));
        }
      } catch (err) {
        applyWidgets(widgetsRef.current.filter((i) => i.id !== tempId));
        toastMutationError(err, "Failed to duplicate widget");
      }
    },
    [
      duplicateWidgetMutation,
      moveWidgetsMutation,
      applyWidgets,
      localOnly,
      localIdPrefix,
      beginLocalEdit,
      markLocalEdit,
    ],
  );

  const replaceWidget = useCallback(
    async (id: string, preset: WidgetPreset): Promise<void> => {
      const prev = widgetsRef.current;
      const source = prev.find((i) => i.id === id);
      if (!source) return;
      const def = registry.get(preset.type);
      const schemaVersion = def?.schemaVersion ?? 1;
      const config = presetInitialConfig(preset);
      if (localOnly) {
        beginLocalEdit();
        applyWidgets(
          prev.map((i) =>
            i.id === id ? { ...i, type: preset.type, config, schemaVersion } : i,
          ),
        );
        markLocalEdit();
        return;
      }
      if (id.startsWith("temp-")) return; // not persisted yet
      // A config edit to the OLD widget still inside the debounce window must
      // not fire after the swap — it would clobber the new module's config
      // with the old one's. Mark it sent so the debounce effect skips it.
      const pending = pendingConfigRef.current;
      if (pending?.id === id) sentConfigRef.current = pending;
      // In place: id and grid cell survive, module swaps.
      applyWidgets(
        prev.map((i) =>
          i.id === id ? { ...i, type: preset.type, config, schemaVersion } : i,
        ),
      );
      try {
        const res = await replaceWidgetMutation({
          variables: {
            id,
            input: {
              type: preset.type,
              config: config as Record<string, unknown>,
              schemaVersion,
            },
          },
        });
        const saved = res.data?.replaceWidget;
        if (!saved) {
          applyWidgets(
            widgetsRef.current.map((i) => (i.id === id ? source : i)),
          );
          toastManager.add({ title: "Failed to replace widget", type: "error" });
          return;
        }
        applyWidgets(
          widgetsRef.current.map((i) => (i.id === id ? toWidget(saved) : i)),
        );
      } catch (err) {
        applyWidgets(widgetsRef.current.map((i) => (i.id === id ? source : i)));
        toastMutationError(err, "Failed to replace widget");
      }
    },
    [registry, replaceWidgetMutation, applyWidgets, localOnly, beginLocalEdit, markLocalEdit],
  );

  const removeWidgetImpl = useCallback(
    (id: string, opts?: { compact?: boolean }, writeThrough = true): GridLayoutItem[] => {
      const shouldCompact = opts?.compact ?? true;
      const prev = widgetsRef.current;
      const layout = prev.map((i) => ({ id: i.id, x: i.x, y: i.y, w: i.w, h: i.h }));
      // Every user-facing removal floats the survivors up so a deleted (or
      // toggled-off) widget never leaves a vertical hole; only the managed
      // maintenance corrections skip the reflow (`compact: false`) so a
      // mid-repair removal doesn't shuffle the board.
      const next = shouldCompact
        ? removeItem(layout, id, GRID_COLUMNS, "gravity")
        : layout.filter((i) => i.id !== id);
      const moves = diffMoves(layout, next);
      const cellById = new Map(next.map((i) => [i.id, i]));
      // ONE commit: deletion + reflow land together so FLIP animates the
      // survivors closing the hole.
      if (!writeThrough) beginLocalEdit();
      applyWidgets(
        prev
          .filter((i) => i.id !== id)
          .map((i) => {
            const cell = cellById.get(i.id);
            return cell && (cell.x !== i.x || cell.y !== i.y)
              ? { ...i, x: cell.x, y: cell.y }
              : i;
          }),
      );
      if (!writeThrough) {
        markLocalEdit();
        return next;
      }
      if (id.startsWith("temp-")) return next; // never reached the server
      removeWidgetMutation({ variables: { id } })
        .then(() => {
          // Delete-then-moves ordering: a failed delete never persists the
          // reflow; failed moves after a landed delete stay the existing
          // fire-and-forget semantics.
          const persisted = moves.filter((m) => !m.id.startsWith("temp-"));
          if (persisted.length > 0) {
            moveWidgetsMutation({ variables: { moves: persisted } }).catch((err) =>
              toastMutationError(err, "Failed to save layout"),
            );
          }
        })
        .catch((err) => {
          applyWidgets(prev); // restore on failure (rolls the reflow back too)
          toastMutationError(err, "Failed to remove widget");
        });
      return next;
    },
    [removeWidgetMutation, moveWidgetsMutation, applyWidgets, beginLocalEdit, markLocalEdit],
  );

  const removeWidget = useCallback(
    (id: string, opts?: { compact?: boolean }): GridLayoutItem[] =>
      removeWidgetImpl(id, opts, !localOnly),
    [removeWidgetImpl, localOnly],
  );

  // Replay an ordered board plan against the server (`widget-board-diff.ts`
  // owns the shape and order: removes → adds with id capture → replaces →
  // configs → one move batch). Shared by `saveToTeam` (baseline → working)
  // and autosave-mode `restoreBoard` (undo/redo). Mutates `widgetsRef` as
  // server ids land; the caller re-syncs state with `applyWidgets` after.
  const commitBoardPlan = useCallback(
    async (plan: ReturnType<typeof diffWidgetBoards>): Promise<void> => {
      // Within the remove / replace / config phases each mutation is
      // independent, so a phase fires concurrently and awaits together — a
      // large-board save costs one round-trip per phase, not one per widget.
      // Adds stay serial: each landing id is captured into `widgetsRef` in
      // plan order. A failure still rejects the whole commit; the caller's
      // recovery re-syncs the baseline from the server, so partial landings
      // are already handled.
      //
      // 1. Removes — a NotFound result means the row is already gone, which
      // is the desired end state; don't abort the batch over it. Local-only
      // ids never reached the server, so there is nothing to remove.
      await Promise.all(
        plan.removes
          .filter((id) => !id.startsWith("temp-"))
          .map((id) => removeWidgetMutation({ variables: { id } })),
      );
      // 2. Adds — capture server ids into the working copy as they land.
      for (const added of plan.adds) {
        const res = await addWidgetMutation({
          variables: {
            view,
            input: {
              type: added.type,
              config: (added.config ?? {}) as Record<string, unknown>,
              x: added.x,
              y: added.y,
              w: added.w,
              h: added.h,
              schemaVersion: added.schemaVersion,
            },
          },
        });
        const saved = res.data?.addWidget;
        if (!saved) throw new Error("Failed to add widget");
        widgetsRef.current = widgetsRef.current.map((i) =>
          i.id === added.id
            ? { ...toWidget(saved), x: added.x, y: added.y, w: added.w, h: added.h }
            : i,
        );
      }
      // 3. Replaces (type changed) on surviving rows.
      await Promise.all(
        plan.replaces.map(async (current) => {
          const res = await replaceWidgetMutation({
            variables: {
              id: current.id,
              input: {
                type: current.type,
                config: (current.config ?? {}) as Record<string, unknown>,
                schemaVersion: current.schemaVersion,
              },
            },
          });
          if (!res.data?.replaceWidget) throw new Error("Failed to replace widget");
        }),
      );
      // 4. Config updates on surviving same-type rows.
      await Promise.all(
        plan.configs.map(async (current) => {
          const res = await updateWidgetConfigMutation({
            variables: {
              id: current.id,
              input: { config: (current.config ?? {}) as Record<string, unknown> },
            },
          });
          if (!res.data?.updateWidgetConfig) throw new Error("Failed to save widget");
        }),
      );
      // 5. One move batch for every surviving rect change (added rows landed
      // at their working position already).
      const persistedMoves = plan.moves.filter((m) => !m.id.startsWith("temp-"));
      if (persistedMoves.length > 0) {
        await moveWidgetsMutation({ variables: { moves: persistedMoves } });
      }
    },
    [
      view,
      addWidgetMutation,
      removeWidgetMutation,
      replaceWidgetMutation,
      updateWidgetConfigMutation,
      moveWidgetsMutation,
    ],
  );

  // ── Staged mode: diff-commit / discard ──────────────────────────────────
  const saveToTeam = useCallback(async (): Promise<void> => {
    if (mode !== "staged" || !dirtyRef.current) return;
    try {
      await commitBoardPlan(diffWidgetBoards(baselineRef.current, widgetsRef.current));
      // Clean first so applyWidgets re-syncs the baseline to the saved board.
      setDirty(false);
      applyWidgets(widgetsRef.current);
    } catch (err) {
      toastMutationError(err, "Failed to save for the team");
      // Part of the batch may have landed. Re-sync the baseline from the
      // server so the next save diffs against reality; KEEP the working copy
      // (and the dirty bar) so the user can retry without losing edits.
      try {
        const res = await refetch();
        const rows = res.data?.widgets;
        if (rows && rows.every((w) => w.view === view)) {
          baselineRef.current = rows.map(toWidget);
        }
      } catch {
        // Refetch failed too — keep the stale baseline; retry re-syncs.
      }
    }
  }, [mode, view, commitBoardPlan, applyWidgets, setDirty, refetch]);

  // ── History restores: wholesale board replace, one op in every mode ─────
  // The undo/redo host hands back a full prior board. Locally it is always
  // ONE commit (FLIP animates the whole delta); persistence follows the mode
  // like every other op — staged flips the save bar, personal schedules the
  // fork snapshot, autosave replays the diff against the server through the
  // same ordered plan `saveToTeam` commits, reverting the working copy if
  // the replay fails.
  const restoreBoard = useCallback(
    async (next: WidgetInstance[]): Promise<void> => {
      const prev = widgetsRef.current;
      if (localOnly) beginLocalEdit();
      applyWidgets(next);
      if (localOnly) {
        markLocalEdit();
        return;
      }
      try {
        await commitBoardPlan(diffWidgetBoards(prev, next));
        applyWidgets(widgetsRef.current); // sync captured server ids
      } catch (err) {
        applyWidgets(prev);
        toastMutationError(err, "Failed to restore layout");
      }
    },
    [localOnly, beginLocalEdit, applyWidgets, markLocalEdit, commitBoardPlan],
  );

  const discardChanges = useCallback(() => {
    if (mode !== "staged" || !dirtyRef.current) return;
    setDirty(false);
    applyWidgets(baselineRef.current);
  }, [mode, setDirty, applyWidgets]);

  // "Save for just me": the staged edits become the personal fork, the
  // template reverts to the canonical baseline. The fork write lands in the
  // Apollo cache synchronously (optimistic `setValue`), so the host's lens
  // flip to personal re-seeds from it immediately.
  const saveAsPersonal = useCallback(() => {
    if (mode !== "staged" || !dirtyRef.current) return;
    setPersonalValue({
      version: PERSONAL_SNAPSHOT_VERSION,
      widgets: widgetsRef.current,
    });
    setDirty(false);
    applyWidgets(baselineRef.current);
  }, [mode, setDirty, applyWidgets, setPersonalValue]);

  // ── Personal mode: fork lifecycle ───────────────────────────────────────
  const resetToDefault = useCallback(() => {
    if (mode !== "personal") return;
    // Clear the pending slot FIRST so an in-flight debounced snapshot can't
    // resurrect the fork after the delete.
    personalPendingRef.current = null;
    personalSentRef.current = null;
    setPersonalPending(null);
    setPersonalValue(null);
    // Drop the working copy back to null; the seeding effect re-seeds from
    // the shared server rows (the fork is gone from the cache synchronously).
    widgetsRef.current = [];
    setWidgetsState(null);
  }, [mode, setPersonalValue]);

  const direct = useMemo(
    () => ({
      seed: seedWidgetsThrough,
      remove: (id: string, opts?: { compact?: boolean }) => {
        removeWidgetImpl(id, opts, true);
      },
      updateConfig: directUpdateConfig,
      move: moveWidgetsThrough,
    }),
    [seedWidgetsThrough, removeWidgetImpl, directUpdateConfig, moveWidgetsThrough],
  );

  return {
    widgets: widgets ?? [],
    initialLoading: widgets === null,
    addWidget,
    seedWidgets,
    duplicateWidget,
    replaceWidget,
    removeWidget,
    updateWidgetConfig,
    moveWidgets,
    restoreBoard,
    dirty,
    saveToTeam,
    discardChanges,
    saveAsPersonal,
    hasPersonalLayer: mode === "personal" && personal.value !== null,
    resetToDefault,
    direct,
  };
}
